import { getMessage } from '@tcc/i18n';
import intersect from "@turf/intersect"
import circle from "@turf/circle"
import { point, multiPolygon, polygon } from "@turf/helpers"
import { COTTON_CROP_CODE, SILAGE_CORN_CROP_CODE } from '../constants/cropCodes';
import { REGION_SHAPES } from '../constants/scouting';
import { ShapeType } from '../types';
/**
 * Get GeoJSON from layer based on the layerType
 * We have to use turfPoint and turfCircle to convert the circle layer to a polygon
 * @param {Object} layer
 * @param {String} layerType
 * @return {Object} - GeoJSON Object
 */
export const getGeoJSON = async (layer, layerType) => {
  if (layerType === 'circle') {
    const CIRCLE_RESOLUTION = 28; // # of vertexes. Want smallest # that still looks smooth.
    const center = layer.getLatLng();
    const centerPoint = point([center.lng, center.lat]);
    const options = { steps: CIRCLE_RESOLUTION, units: 'kilometers' };
    return circle(centerPoint, layer.getRadius() / 1000, options);
  }
  const geoJSON = layer.toGeoJSON();

  return geoJSON;
};

/**
 * Get message for region drawing status and shape.
 * @param {Boolean} isRegionCreated - Region is already created but not saved
 * @param {Boolean} isRegionStarted - Region drawing is started but not created
 * @param {String} shape - Shape selected to start drawing
 * @param {Number} vertexDrawn - Number of vertex drawn if shape is polygon
 * @return {String} - Message to show from i18n
 */
export const getRegionMessage = (
  isRegionCreated,
  isRegionStarted,
  shape,
  vertexDrawn
) => {
  if (!isRegionCreated && !isRegionStarted) {
    return '';
  }

  switch (shape) {
    case REGION_SHAPES.PIVOT:
      if (isRegionStarted) {
        return getMessage('regionDraw.startedPivot');
      }
      return getMessage('regionDraw.createdPivot');
    case REGION_SHAPES.RECTANGLE:
      if (isRegionStarted) {
        return getMessage('regionDraw.startedRectangle');
      }
      return getMessage('regionDraw.createdRectangle');
    case REGION_SHAPES.POLYGON:
      if (isRegionStarted && vertexDrawn < 3) {
        return getMessage('regionDraw.startedPolygon');
      }
      return getMessage('regionDraw.createdPolygon');
    default:
      return '';
  }
};

/**
 * Map tags ids and values
 * @param {Array} tagList - List of all tags
 * @param {Array} selectedTags - List of all selected tags to map
 * @param {Boolean} isLoading - Form is loading
 * @return {Array} - List of mapped selected tags
 */
export const mapTags = (tagList, selectedTags, isLoading = false) => {
  const tagObject = tagList.reduce((obj, element) => {
    if (isLoading) {
      return { ...obj, [element.text]: element.id };
    }

    return { ...obj, [element.id]: element.text };
  }, {});
  return selectedTags.map((val) => tagObject[val] || '');
};


/**
 * 
 * @param {Array} region 
 * @returns {Object}
 */
const getRegionShape = (region) => {
  if (region.type === ShapeType.MULTI_POLYGON) {
    return multiPolygon(region.coordinates);
  }

  return polygon(region.coordinates);
}

/**
 * Return a boolean based on the intersection between the field boundary and the region
 * This method help us to check if the region is inside (intersects) the field boundary.
 * @param  {Array} fieldBoundary
 * @param  {Array} region
 * @return {Object} true or false
 */
export const isRegionInsideField = async (fieldBoundary, region) => {
  const fieldMultiPolygon = multiPolygon(fieldBoundary.coordinates);
  const regionPolygon = getRegionShape(region);

  return Boolean(intersect(fieldMultiPolygon, regionPolygon));
};

export const isValidHarvestData = (harvestState) => {
  if(!harvestState) {
    return false;
  }

  const harvestData = {
    area: harvestState.area,
    avgMoisture: harvestState.avgMoisture,
    avgYield: harvestState.avgYield,
  }
  
  return !Object.values(harvestData).every((v) => !v.q || v.q === '0');
}

/**
 * Return an average yield string based on the crop code & feature flag
 * @param  {String} cropCode
 * @param  {Boolean} isSilageCrop
 * @return {String}
 */
export const getAverageYieldText = (
  cropCode,
) => {
  switch (cropCode) {
    case COTTON_CROP_CODE:
      return 'regions.report.modal.cottonAverageYield'
    case SILAGE_CORN_CROP_CODE:
      return 'regions.report.modal.averageDryYield';
    default:
      return 'regions.report.modal.averageYield';
  }
};

export default isRegionInsideField;
